/**
 * In-browser live voice via OpenAI Realtime over WebRTC.
 *
 * Flow (per OpenAI Realtime WebRTC guide):
 *   1. ask OUR server for a short-lived ephemeral token (the real API key never
 *      reaches the browser). Server endpoint: POST /api/realtime-token.
 *   2. getUserMedia(audio) -> add mic track to an RTCPeerConnection.
 *   3. remote audio track -> <audio> element (the attacker's voice).
 *   4. data channel "oai-events" carries transcripts + control events.
 *   5. POST our SDP offer to the Realtime endpoint with the ephemeral token,
 *      apply the SDP answer.
 *
 * Audio media flows browser <-> OpenAI directly (NOT through our VPS), so this
 * scales to thousands of learners without server media load.
 */

export interface RealtimeSessionConfig {
  /** system prompt = the scenario engine's attacker prompt. */
  instructions: string;
  /** provider preset voice for the attacker. */
  voice?: string;
  /** ISO language hint. */
  language?: string;
  /** TYPED FALLBACK: when false, skip the microphone entirely — the learner
   *  types instead (see `sendText`), but still HEARS the attacker (a recvonly
   *  audio transceiver is added). Default true (full voice). */
  mic?: boolean;
}

import { apiUrl, getHostConfig } from "../lib/hostConfig";

export type RealtimeEvent = Record<string, unknown> & { type: string };

export interface RealtimeHandle {
  stop: () => void;
  /** send a client event (e.g. session.update) over the data channel. */
  send: (event: RealtimeEvent) => void;
  /** TYPED FALLBACK: submit a learner text message and request the attacker's
   *  reply (creates a user message item + a response). No-op until the data
   *  channel is open. */
  sendText: (text: string) => void;
  /** the <audio> element playing the attacker voice (already playing). */
  audioEl: HTMLAudioElement;
  /** ECHO GUARD: mute/unmute the outgoing mic track without tearing down the
   *  peer connection. See useVoiceCall.ts — the caller gates this to the
   *  attacker's own speaking window so the caller's voice coming out of the
   *  learner's speakers can't leak back into the mic and false-trigger
   *  server-VAD mid-sentence (same failure mode as the production voice
   *  gateway's half-duplex input gate, ch.11). No-op in typed mode (no mic
   *  track exists). */
  setMicEnabled: (enabled: boolean) => void;
}

// GA WebRTC SDP exchange endpoint (replaces the deprecated `/v1/realtime?model=`).
// The model + session config are already bound to the ephemeral secret at mint time,
// so no `?model=` query and no `OpenAI-Beta` header are needed.
const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

export async function startRealtimeCall(
  cfg: RealtimeSessionConfig,
  onEvent: (e: RealtimeEvent) => void
): Promise<RealtimeHandle> {
  // 1) ephemeral token from our server. Standalone: /api/realtime-token (token
  //    server). Cyberfrog host mode: /sim/realtime-token on the learner origin.
  const host = getHostConfig();
  const rtPath = host.hosted ? "/sim/realtime-token" : "/api/realtime-token";
  const tokenResp = await fetch(apiUrl(rtPath), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // Host mode gates /sim/realtime-token on the assignment token.
    body: JSON.stringify(
      host.hosted && host.token ? { ...cfg, token: host.token } : cfg
    ),
  });
  if (!tokenResp.ok) {
    throw new Error(`token endpoint failed: ${tokenResp.status} ${await tokenResp.text()}`);
  }
  const tokenJson = await tokenResp.json();
  // GA returns the ephemeral secret at top-level `value`; keep the legacy reader as a fallback.
  const ephemeral: string = tokenJson?.value ?? tokenJson?.client_secret?.value;
  if (!ephemeral) throw new Error("no ephemeral token in response");

  // 2) peer connection + mic
  const pc = new RTCPeerConnection();
  const audioEl = document.createElement("audio");
  audioEl.autoplay = true;

  pc.ontrack = (e) => {
    audioEl.srcObject = e.streams[0];
  };

  // TYPED FALLBACK: mic:false skips getUserMedia entirely (no permission
  // prompt) but still receives the attacker's voice via a recvonly transceiver.
  const useMic = cfg.mic !== false;
  let mic: MediaStream | null = null;
  if (useMic) {
    mic = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1, sampleRate: 24000 },
    });
    mic.getTracks().forEach((t) => pc.addTrack(t, mic!));
  } else {
    pc.addTransceiver("audio", { direction: "recvonly" });
  }

  // 3) data channel for events (transcripts, etc.)
  const dc = pc.createDataChannel("oai-events");
  dc.onmessage = (ev) => {
    try {
      onEvent(JSON.parse(ev.data));
    } catch {
      /* ignore non-JSON keepalives */
    }
  };
  // The attacker SPEAKS FIRST: as soon as the channel is open, trigger the
  // model's opening turn (its system prompt tells it to open the call). Without
  // this, server-VAD waits for the learner to talk first (dead air on answer).
  dc.onopen = () => {
    try {
      dc.send(JSON.stringify({ type: "response.create" }));
    } catch {
      /* ignore */
    }
  };
  // No dc.onopen session.update: instructions, voice, transcription and VAD are all
  // bound to the ephemeral secret at mint time (token-server.mjs), which is also the
  // tamper-proof boundary — the browser must not be able to WIDEN the session at
  // connect time. Later, mid-call, OUR OWN trusted client code (useVoiceCall.ts's
  // playbook stage tracker) does send narrowing `session.update`s via `send()` below
  // to re-steer which ladder stage the live model should be focused on — that's a
  // steering aid over content this same client already fully controls, not a
  // learner-facing widening path.


  // 4) SDP offer -> Realtime /calls endpoint -> answer
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  const sdpResp = await fetch(REALTIME_CALLS_URL, {
    method: "POST",
    body: offer.sdp,
    headers: { Authorization: `Bearer ${ephemeral}`, "Content-Type": "application/sdp" },
  });
  if (!sdpResp.ok) {
    pc.close();
    throw new Error(`realtime SDP failed: ${sdpResp.status} ${await sdpResp.text()}`);
  }
  const answer = { type: "answer" as const, sdp: await sdpResp.text() };
  await pc.setRemoteDescription(answer);

  return {
    audioEl,
    setMicEnabled: (enabled) => {
      mic?.getTracks().forEach((t) => {
        t.enabled = enabled;
      });
    },
    send: (event) => {
      if (dc.readyState === "open") dc.send(JSON.stringify(event));
    },
    sendText: (text) => {
      if (dc.readyState !== "open") return;
      dc.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "user",
            content: [{ type: "input_text", text }],
          },
        })
      );
      dc.send(JSON.stringify({ type: "response.create" }));
    },
    stop: () => {
      try {
        mic?.getTracks().forEach((t) => t.stop());
        dc.close();
        pc.close();
        audioEl.srcObject = null;
      } catch {
        /* noop */
      }
    },
  };
}
